import * as React from 'react'

import { Alert, Box, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  TimesheetOvertimeRequestDocument,
  TimesheetOvertimeRequestFragment,
  UpdateTimesheetOvertimeRequestMutationVariables,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import DurationInput from 'modules/core/components/DurationInput'
import TextArea from 'modules/core/components/TextArea'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useFormContext } from 'modules/core/hooks/useForm'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  isEditing?: boolean
  isPending: boolean
  canApprove: boolean
  employeeId: number
  setLoading?: (loading: boolean) => void
  setOvertimeRequest?: (overtimeRequest?: TimesheetOvertimeRequestFragment | null) => void
}

const OverTimeRequestForm = ({
  isEditing,
  isPending,
  canApprove,
  employeeId,
  setLoading = () => {},
  setOvertimeRequest = () => {},
}: Props) => {
  const form = useFormContext<UpdateTimesheetOvertimeRequestMutationVariables>()

  const [fetchOvertimeRequest, { data, networkActivity: loadingOvertimeRequest }] = useLazyQuery(
    TimesheetOvertimeRequestDocument
  )
  const overtimeRequests = data?.attendance?.overtimeRequests
  const overtimeRequest = overtimeRequests?.[0]

  useEffect(() => {
    if (!isEditing)
      fetchOvertimeRequest({ variables: { employeeIds: [employeeId], startOn: form.values.date } })
  }, [])

  useEffect(() => {
    if (loadingOvertimeRequest) {
      setLoading(true)
    } else {
      setLoading(false)
      setOvertimeRequest(overtimeRequest)
    }
  }, [loadingOvertimeRequest, overtimeRequest?.id])

  const dateHasRequest = !!overtimeRequest && overtimeRequest.id != form.values.id

  const warningTranslations: Record<string, string> = {
    pending: i18n.t('time_tracking.modals.overtime_request.warning.pending'),
    approved: i18n.t('time_tracking.modals.overtime_request.warning.approved'),
    rejected: i18n.t('time_tracking.modals.overtime_request.warning.rejected'),
  }

  return (
    <Box width="full" padding="s2" borderRadius={{ all: 'abs008' }} gap="s16">
      <Box flexDirection="row" alignItems="center" justifyContent="flexStart" gap="s24">
        <DayPicker
          mandatory
          label={i18n.t('time_tracking.modals.overtime_request.date')}
          valueFormat="backend"
          value={form.values.date}
          error={form.errors.date}
          mode="single"
          format="ui"
          onChange={(dateValue) => {
            form.setValues({ ...form.values, date: dateValue })
            fetchOvertimeRequest({ variables: { employeeIds: [employeeId], startOn: dateValue } })
          }}
        />
        <Box width="full">
          <DurationInput
            label={i18n.t('time_tracking.modals.overtime_request.hours_amount')}
            minutes={(form.values.hoursAmount || 0) * 60}
            onChange={(value: number) => form.setFieldValue('hoursAmount', value / 60)}
          />
        </Box>
      </Box>
      {!canApprove && dateHasRequest && (
        <Alert>
          <Box alignItems="flexStart" flexDirection="row" gap="s4">
            <Box flexNoShrink>
              <Icon.Medium icon="alert-circle" color="grey800" />
            </Box>
            <Text.Normal>
              {overtimeRequest.status ? warningTranslations[overtimeRequest.status] : ''}
            </Text.Normal>
          </Box>
        </Alert>
      )}
      <TextArea
        placeholder={
          isPending ? i18n.t('time_tracking.modals.overtime_request.description_placeholder') : ''
        }
        label={i18n.t('time_tracking.modals.overtime_request.description_field')}
        value={form.values.description || ''}
        onChange={(description) => form.setFieldValue('description', description)}
      />
      {isEditing && (
        <TextArea
          placeholder={
            canApprove && isPending
              ? i18n.t('time_tracking.modals.overtime_request.description_placeholder')
              : ''
          }
          label={i18n.t('time_tracking.modals.overtime_request.additional_comments')}
          value={form.values.reason || ''}
          onChange={(reason) => form.setFieldValue('reason', reason)}
          error={form.errors.reason}
          readonly={!canApprove || !isPending}
        />
      )}
    </Box>
  )
}

export default observer(OverTimeRequestForm)
